import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

export const TERMSHEET_SIGNATURE_IMAGE_URL = '/ashwanee_signature.png'

const sanitizePdfFileNamePart = (value: string) => value.replace(/[<>:"/\\|?*]/g, '').trim()

export const buildTermsheetPdfFileName = (userName: string, projectName: string) => {
  const safeProjectName = sanitizePdfFileNamePart(projectName) || 'Project'
  const safeUserName = sanitizePdfFileNamePart(userName)

  if (safeUserName) {
    return `Termsheet Agreement for ${safeUserName} - ${safeProjectName}.pdf`
  }

  return `Termsheet Agreement for ${safeProjectName}.pdf`
}

const TERMSHEET_PDF_RENDER_WIDTH = 794
const TERMSHEET_PDF_PADDING_PX = 32
const TERMSHEET_PDF_MARGIN_PT = 32
const IMAGE_LOAD_TIMEOUT_MS = 15000
const SVG_SIGNATURE_REGEX = /<svg[^>]*>[\s\S]*?<image\b[\s\S]*?<\/svg>/gi

const normalizeImageUrl = (url: string) => {
  try {
    return new URL(url, window.location.origin).href
  } catch {
    return url
  }
}

const waitForImageLoad = (img: HTMLImageElement, timeoutMs = IMAGE_LOAD_TIMEOUT_MS) =>
  new Promise<void>((resolve) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve()
      return
    }

    const done = () => {
      clearTimeout(timer)
      img.removeEventListener('load', done)
      img.removeEventListener('error', done)
      resolve()
    }

    const timer = setTimeout(done, timeoutMs)
    img.addEventListener('load', done)
    img.addEventListener('error', done)
  })

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read image blob'))
    reader.readAsDataURL(blob)
  })

const imageElementToDataUrl = (img: HTMLImageElement): string | null => {
  if (!img.complete || img.naturalWidth === 0) return null

  try {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(img, 0, 0)
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url, { credentials: 'include' })
    if (!response.ok) return null
    return await blobToDataUrl(await response.blob())
  } catch {
    return null
  }
}

const prepareImagesForCapture = async (container: HTMLElement, sourceRoot?: HTMLElement | null) => {
  const images = Array.from(container.querySelectorAll('img'))
  if (images.length === 0) return

  const sourceImages = sourceRoot ? Array.from(sourceRoot.querySelectorAll('img')) : []
  const sourceBySrc = new Map(sourceImages.map((img) => [normalizeImageUrl(img.src), img]))

  await Promise.all(
    images.map(async (img) => {
      const rawSrc = img.getAttribute('src') || img.src
      if (!rawSrc || rawSrc.startsWith('data:')) return

      const normalizedSrc = normalizeImageUrl(rawSrc)
      const sourceImg = sourceBySrc.get(normalizedSrc)

      if (sourceImg && !sourceImg.complete) {
        await waitForImageLoad(sourceImg)
      }

      const dataUrl =
        (sourceImg && imageElementToDataUrl(sourceImg)) ||
        (await fetchImageAsDataUrl(normalizedSrc))

      if (dataUrl) {
        img.src = dataUrl
        await waitForImageLoad(img)
        return
      }

      img.removeAttribute('crossorigin')
      if (sourceImg?.src) {
        img.src = sourceImg.src
      }
      await waitForImageLoad(img)
    }),
  )
}

const buildSignatureImgTag = (dataUrl: string, width = '500') =>
  `<img src="${dataUrl}" alt="Signature" style="max-width:${width}px;width:100%;height:auto;display:block;" />`

const replaceSvgSignaturesWithImage = async (html: string, signatureImageUrl: string) => {
  if (!SVG_SIGNATURE_REGEX.test(html)) return html

  SVG_SIGNATURE_REGEX.lastIndex = 0
  const dataUrl = await fetchImageAsDataUrl(normalizeImageUrl(signatureImageUrl))
  if (!dataUrl) return html

  SVG_SIGNATURE_REGEX.lastIndex = 0
  return html.replace(SVG_SIGNATURE_REGEX, (block) => {
    const widthMatch = block.match(/width=["'](\d+)["']/i)
    return buildSignatureImgTag(dataUrl, widthMatch?.[1] || '500')
  })
}

const getDomBreakPoints = (container: HTMLElement) => {
  const breakSelector = 'h1,h2,h3,h4,h5,h6,p,div,section,article,hr,ul,ol,li,table,thead,tbody,tr'
  const blockNodes = Array.from(container.querySelectorAll(breakSelector)) as HTMLElement[]

  return Array.from(
    new Set(
      blockNodes
        .map((el) => {
          try {
            return Math.max(0, el.offsetTop)
          } catch {
            return 0
          }
        })
        .filter((y) => Number.isFinite(y)),
    ),
  ).sort((a, b) => a - b)
}

const createTermsheetCaptureContainer = (html: string) => {
  const container = document.createElement('div')
  container.className = 'self-declaration-wrapper-text'
  container.innerHTML = html
  container.style.position = 'fixed'
  container.style.left = '-10000px'
  container.style.top = '0'
  container.style.width = `${TERMSHEET_PDF_RENDER_WIDTH}px`
  container.style.maxWidth = `${TERMSHEET_PDF_RENDER_WIDTH}px`
  container.style.minWidth = `${TERMSHEET_PDF_RENDER_WIDTH}px`
  container.style.padding = `${TERMSHEET_PDF_PADDING_PX}px`
  container.style.boxSizing = 'border-box'
  container.style.background = '#ffffff'
  container.style.color = '#545a5e'
  container.style.fontSize = '16px'
  container.style.lineHeight = '1.5'
  container.style.overflow = 'visible'
  container.style.wordBreak = 'break-word'
  document.body.appendChild(container)
  return container
}

const addCanvasSliceToPdf = ({
  pdf,
  canvas,
  startY,
  sliceHeightPx,
  pageNumber,
  margin,
  usableWidth,
  pxPerPt,
}: {
  pdf: jsPDF
  canvas: HTMLCanvasElement
  startY: number
  sliceHeightPx: number
  pageNumber: number
  margin: number
  usableWidth: number
  pxPerPt: number
}) => {
  const pageCanvas = document.createElement('canvas')
  pageCanvas.width = canvas.width
  pageCanvas.height = sliceHeightPx
  const pageCtx = pageCanvas.getContext('2d')
  if (!pageCtx) return false

  pageCtx.fillStyle = '#ffffff'
  pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
  pageCtx.drawImage(
    canvas,
    0,
    startY,
    canvas.width,
    sliceHeightPx,
    0,
    0,
    canvas.width,
    sliceHeightPx,
  )

  const imageData = pageCanvas.toDataURL('image/png')
  const imageHeightPt = sliceHeightPx / pxPerPt
  if (pageNumber > 0) pdf.addPage()
  pdf.addImage(imageData, 'PNG', margin, margin, usableWidth, imageHeightPt)
  return true
}

const addCanvasToMultiPagePdf = ({
  pdf,
  canvas,
  container,
  margin,
  usableWidth,
  usableHeight,
}: {
  pdf: jsPDF
  canvas: HTMLCanvasElement
  container: HTMLElement
  margin: number
  usableWidth: number
  usableHeight: number
}) => {
  const pxPerPt = canvas.width / usableWidth
  const pageSliceHeightPx = Math.floor(usableHeight * pxPerPt)
  const domToCanvasScale = canvas.width / container.scrollWidth
  const pageSliceHeightDom = Math.floor(pageSliceHeightPx / domToCanvasScale)
  const minSliceHeightDom = Math.floor(pageSliceHeightDom * 0.7)
  const breakPointsDom = getDomBreakPoints(container)
  const overlapPx = Math.max(2, Math.floor(4 * domToCanvasScale))

  let renderedHeightPx = 0
  let renderedHeightDom = 0
  let pageNumber = 0

  while (renderedHeightPx < canvas.height) {
    const remainingCanvasPx = canvas.height - renderedHeightPx

    if (remainingCanvasPx <= pageSliceHeightPx) {
      addCanvasSliceToPdf({
        pdf,
        canvas,
        startY: renderedHeightPx,
        sliceHeightPx: remainingCanvasPx,
        pageNumber,
        margin,
        usableWidth,
        pxPerPt,
      })
      break
    }

    const targetEndDom = renderedHeightDom + pageSliceHeightDom
    const minEndDom = renderedHeightDom + minSliceHeightDom
    let bestBreakDom = targetEndDom
    for (let i = 0; i < breakPointsDom.length; i += 1) {
      const y = breakPointsDom[i]
      if (y <= targetEndDom && y >= minEndDom) bestBreakDom = y
      if (y > targetEndDom) break
    }

    const sliceHeightPx = Math.max(
      1,
      Math.floor((bestBreakDom - renderedHeightDom) * domToCanvasScale),
    )
    const isAdded = addCanvasSliceToPdf({
      pdf,
      canvas,
      startY: renderedHeightPx,
      sliceHeightPx,
      pageNumber,
      margin,
      usableWidth,
      pxPerPt,
    })
    if (!isAdded) break

    renderedHeightPx += sliceHeightPx
    renderedHeightDom += Math.floor(sliceHeightPx / domToCanvasScale)
    if (renderedHeightPx < canvas.height) {
      renderedHeightPx = Math.max(0, renderedHeightPx - overlapPx)
    }
    pageNumber += 1
  }
}

export const exportTermsheetHtmlToPdf = async ({
  html,
  fileName,
  sourceElement,
  signatureImageUrl = TERMSHEET_SIGNATURE_IMAGE_URL,
}: {
  html: string
  fileName: string
  sourceElement?: HTMLElement | null
  signatureImageUrl?: string
}) => {
  let container: HTMLDivElement | null = null

  try {
    const processedHtml = await replaceSvgSignaturesWithImage(html, signatureImageUrl)
    container = createTermsheetCaptureContainer(processedHtml)
    await prepareImagesForCapture(container, sourceElement)

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: false,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: container.scrollWidth,
      height: container.scrollHeight,
      windowWidth: container.scrollWidth,
      windowHeight: container.scrollHeight,
      imageTimeout: IMAGE_LOAD_TIMEOUT_MS,
    })

    const pdf = new jsPDF('p', 'pt', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = TERMSHEET_PDF_MARGIN_PT
    const usableWidth = pageWidth - margin * 2
    const usableHeight = pageHeight - margin * 2

    addCanvasToMultiPagePdf({
      pdf,
      canvas,
      container,
      margin,
      usableWidth,
      usableHeight,
    })

    pdf.save(fileName)
  } finally {
    if (container && document.body.contains(container)) {
      document.body.removeChild(container)
    }
  }
}
